import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  BarChart, Bar, Tooltip as RTooltip, Legend,
} from "recharts";
import { AlertTriangle, TrendingUp, Activity, ArrowUpRight } from "lucide-react";
import { api, useFetch } from "@/lib/api";

const TTStyle = { background: "#0A0A0F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", fontSize: "12px" };

function RiskCard({ agent }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-sm bg-red-500/5 border border-red-500/10" data-testid={`risk-card-${agent.agent_id}`}>
      <div className="w-8 h-8 rounded-sm bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <AlertTriangle className="w-4 h-4 text-red-400" strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <p className="font-mono text-sm font-medium">{agent.agent_name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">Risk Score: <span className="text-red-400 font-mono font-medium">{agent.risk_score}</span></p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {agent.factors.map((f, i) => (
            <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-red-500/10 text-red-400 border border-red-500/10">{f}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const { data: perfData, loading: perfLoading } = useFetch(() => api.getPerformanceMetrics());
  const { data: predictions, loading: predLoading } = useFetch(() => api.getPredictions());
  const { data: taskDist } = useFetch(() => api.getTaskDistribution());

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider">ANALYTICS & PREDICTIONS</h1>
        <p className="text-sm text-muted-foreground mt-1">Performance trends, forecasts, and risk analysis</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass-panel opacity-0 animate-fade-in">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-neon-cyan" /> SUCCESS RATE TREND
            </CardTitle>
          </CardHeader>
          <CardContent>
            {perfLoading ? <div className="h-64 skeleton-shimmer rounded-sm" /> : (
              <div className="h-[180px] sm:h-[220px] md:h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={perfData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} interval={4} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} domain={[80, 100]} />
                  <RTooltip contentStyle={TTStyle} />
                  <Line type="monotone" dataKey="success_rate" stroke="#D4FF3A" strokeWidth={2} dot={false} name="Success %" />
                </LineChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-panel opacity-0 animate-fade-in" style={{ animationDelay: "0.05s" }}>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
              <Activity className="w-4 h-4 text-neon-violet" /> ERROR RATE TREND
            </CardTitle>
          </CardHeader>
          <CardContent>
            {perfLoading ? <div className="h-64 skeleton-shimmer rounded-sm" /> : (
              <div className="h-[180px] sm:h-[220px] md:h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={perfData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} interval={4} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <RTooltip contentStyle={TTStyle} />
                  <Line type="monotone" dataKey="error_rate" stroke="#FF1744" strokeWidth={2} dot={false} name="Error %" />
                </LineChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass-panel opacity-0 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-neon-cyan" /> WORKLOAD FORECAST (7D)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {predLoading ? <div className="h-64 skeleton-shimmer rounded-sm" /> : (
              <div className="h-[180px] sm:h-[220px] md:h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={predictions?.forecast || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <RTooltip contentStyle={TTStyle} />
                  <Bar dataKey="predicted_tasks" fill="#D4FF3A" opacity={0.6} radius={[2, 2, 0, 0]} name="Predicted Tasks" />
                  <Bar dataKey="predicted_load" fill="#7000FF" opacity={0.6} radius={[2, 2, 0, 0]} name="Predicted Load %" />
                  <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "JetBrains Mono" }} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-panel opacity-0 animate-fade-in" style={{ animationDelay: "0.15s" }}>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
              TASK BY TYPE
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[180px] sm:h-[220px] md:h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={taskDist?.by_type || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <YAxis dataKey="name" type="category" stroke="#52525B" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} width={100} />
                <RTooltip contentStyle={TTStyle} />
                <Bar dataKey="value" fill="#7000FF" opacity={0.7} radius={[0, 2, 2, 0]} name="Count" />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass-panel opacity-0 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" /> AT-RISK AGENTS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {predLoading ? <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 skeleton-shimmer rounded-sm" />)}</div> : (
              <div className="space-y-3">
                {(predictions?.at_risk_agents || []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8 font-mono">No at-risk agents detected</p>
                )}
                {(predictions?.at_risk_agents || []).map((a) => <RiskCard key={a.agent_id} agent={a} />)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-panel opacity-0 animate-fade-in" style={{ animationDelay: "0.25s" }}>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium">
              RECOMMENDATIONS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {predLoading ? <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-8 skeleton-shimmer rounded-sm" />)}</div> : (
              <div className="space-y-2">
                {(predictions?.recommendations || []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8 font-mono">No recommendations at this time</p>
                )}
                {(predictions?.recommendations || []).map((rec, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-sm bg-neon-cyan/5 border border-neon-cyan/10">
                    <div className="w-5 h-5 rounded-full bg-neon-cyan/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-mono text-neon-cyan font-bold">{i + 1}</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{rec}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
